import { ChatMessage, SiteContent } from '../types/chat';

export class NetlifyChatService {
  private static instance: NetlifyChatService;
  private conversationHistory: ChatMessage[] = [];

  public static getInstance(): NetlifyChatService {
    if (!NetlifyChatService.instance) {
      NetlifyChatService.instance = new NetlifyChatService();
    }
    return NetlifyChatService.instance;
  }

  private getFunctionUrl(): string {
    // Check if we're in development mode
    if (typeof window !== 'undefined') {
      const currentProtocol = window.location.protocol;
      const currentHostname = window.location.hostname;
      const netlifyDevPort = '8888'; // Default Netlify dev port
      
      // If we're on localhost, use the development URL
      if (currentHostname === 'localhost' || currentHostname === '127.0.0.1') {
        const functionUrl = `${currentProtocol}//${currentHostname}:${netlifyDevPort}/.netlify/functions/chat`;
        return functionUrl;
      }
    }
    
    // In production, use relative path
    return '/.netlify/functions/chat';
  }

  public async sendMessage(userMessage: string): Promise<{ 
    message: string; 
    redirectToHubSpot?: boolean; 
    hubSpotMessage?: string; 
    retryAfter?: number;
    requiresVerification?: boolean;
    challenge?: string;
    challengeAnswer?: string;
  }> {
    try {
      // Add user message to history
      this.conversationHistory.push({
        id: Date.now().toString(),
        content: userMessage,
        role: 'user',
        timestamp: new Date()
      });

      // Prepare conversation history for the API
      const conversationHistory = this.conversationHistory.slice(-10).map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      // Get the correct function URL
      const functionUrl = this.getFunctionUrl();

      // Call the Netlify Function
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage,
          conversationHistory
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle rate limit and other errors with HubSpot redirect
        if (data.redirectToHubSpot) {
          return {
            message: data.hubSpotMessage || 'Please use our HubSpot chat for immediate assistance.',
            redirectToHubSpot: true,
            hubSpotMessage: data.hubSpotMessage,
            retryAfter: data.retryAfter
          };
        }
        
        // Handle bot detection and challenge responses
        if (data.errorCode === 'bot_detected' || data.errorCode === 'suspicious_behavior') {
          throw new Error('Access denied. Bot activity detected.');
        }
        
        if (data.errorCode === 'human_verification_required') {
          return {
            message: data.error,
            challenge: data.challenge,
            challengeAnswer: data.challengeAnswer,
            requiresVerification: true
          };
        }
        
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      if (data.error) {
        throw new Error(data.error);
      }

      const assistantMessage = data.message;

      // Add assistant response to history
      this.conversationHistory.push({
        id: (Date.now() + 1).toString(),
        content: assistantMessage,
        role: 'assistant',
        timestamp: new Date()
      });

      // Keep only last 10 messages to manage context length
      if (this.conversationHistory.length > 10) {
        this.conversationHistory = this.conversationHistory.slice(-10);
      }

      return { message: assistantMessage };

    } catch (error) {
      console.error('Error calling Netlify Function:', error);
      throw new Error('Failed to get response from AI assistant. Please try again later.');
    }
  }

  public clearHistory(): void {
    this.conversationHistory = [];
  }
}

export default NetlifyChatService.getInstance(); 